import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Box, Stack} from '@dropbox/dig-foundations';
import {Layout} from 'components/DSYS/Layout';
import type {ComponentProps, CSSProperties, ReactNode} from 'react';

import {SectionLabel} from './SectionLabel';
import {UpdateBox} from './UpdateCard';

type SkeletonWidth = ComponentProps<typeof Skeleton.Box>['width'];

// Skeleton widths are max-widths, which collapse in a flex row; this holds one.
const FixedWidth = ({width, children}: {width: SkeletonWidth; children: ReactNode}) => (
  <Box style={{width, flexShrink: 0}}>{children}</Box>
);

// Header geometry, measured against the loaded page (px).
const HEADER = {
  titleLine: 26,
  heroGap: 8,
  objectiveLine: 22,
  objectiveToProperties: 23,
  propertiesRow: 32,
  propertiesBottom: 10,
};

const SkeletonLine = ({
  height,
  style,
  children,
}: {
  height: number;
  style?: CSSProperties;
  children: ReactNode;
}) => (
  <Box display="flex" alignItems="center" style={{height, ...style}}>
    {children}
  </Box>
);

// The properties line: status button, readiness, privacy, DRI | count, resources.
const PROPERTY_WIDTHS: SkeletonWidth[] = [112, 128, 72, 144, 112];
// Matches UpdateHistory: a 128px list of 32px rows (4px apart) beside the box.
const HISTORY_ROW_WIDTHS: SkeletonWidth[] = [52, 52, 44];
const HISTORY_LIST_WIDTH_PX = 128;
const HISTORY_BOX_MIN_HEIGHT_PX = 3 * 32 + 2 * 4;
// The Metrics table: a 40px header and ~67px rows (title + byline).
const METRICS_HEADER_PX = 40;
const METRIC_ROW_PX = 67;
const METRIC_ROW_WIDTHS: SkeletonWidth[] = [240, 300, 200];

/**
 * /workstreams detail: the page's shape while the workstream loads, in
 * WorkstreamDetailView's layout (title / objective / properties line, Updates,
 * then Metrics), so content lands in place instead of replacing a spinner. The
 * section labels are static, so they render for real.
 */
export const WorkstreamDetailSkeleton = ({indent}: {indent?: number}) => (
  <Box aria-busy="true" aria-label="Loading workstream" paddingY="24" style={{minWidth: 0}}>
    {/* The page's own frame: its gutter and reading cap, `indent` in from the
        gutter when it has a back arrow, so the bars sit where the page lands. */}
    <Layout.MaxWidth width="reading" inset indent={indent}>
      <Stack gap="24">
        {/* Line boxes sized like the real header, so the bars sit where the text
          lands: the 18px Title's 26px line, 8px hero gap, the objective's line,
          then the properties row (32px buttons) and the space the row leaves. */}
        <Layout.MaxWidth width="reading">
          <SkeletonLine height={HEADER.titleLine}>
            <Skeleton.Text size="large" width={320} />
          </SkeletonLine>
          <SkeletonLine height={HEADER.objectiveLine} style={{marginTop: HEADER.heroGap}}>
            <Skeleton.Text width={280} />
          </SkeletonLine>
          <Box
            display="flex"
            alignItems="center"
            style={{
              gap: 24,
              height: HEADER.propertiesRow,
              marginTop: HEADER.objectiveToProperties,
              marginBottom: HEADER.propertiesBottom,
            }}
          >
            {PROPERTY_WIDTHS.map((width, index) => (
              <FixedWidth key={index} width={width}>
                <Skeleton.Box height={24} borderRadius="Medium" />
              </FixedWidth>
            ))}
          </Box>
        </Layout.MaxWidth>

        <Stack gap="32">
          <Layout.MaxWidth width="reading">
            <SectionLabel>Updates</SectionLabel>
            <Box display="flex" alignItems="stretch" style={{gap: 16}}>
              <Stack gap="4" style={{width: HISTORY_LIST_WIDTH_PX, flexShrink: 0}}>
                {HISTORY_ROW_WIDTHS.map((width, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    paddingX="8"
                    style={{gap: 8, minHeight: 32}}
                  >
                    <FixedWidth width={12}>
                      <Skeleton.Box height={12} borderRadius="Circular" />
                    </FixedWidth>
                    <Skeleton.Text size="small" width={width} />
                  </Box>
                ))}
              </Stack>
              <UpdateBox minHeight={HISTORY_BOX_MIN_HEIGHT_PX}>
                <Stack gap="12">
                  <Skeleton.Text size="small" width={200} />
                  <Skeleton.Text />
                  <Skeleton.Text width={240} />
                </Stack>
              </UpdateBox>
            </Box>
          </Layout.MaxWidth>

          <Box>
            <SectionLabel>Metrics</SectionLabel>
            {/* The table's shape: a header row, then metric rows (title + byline). */}
            <Box
              borderStyle="Solid"
              borderColor="Border Subtle"
              borderWidth="1"
              borderRadius="Medium"
              style={{overflow: 'hidden'}}
            >
              <Box
                display="flex"
                alignItems="center"
                paddingX="8"
                borderColor="Border Subtle"
                style={{
                  height: METRICS_HEADER_PX,
                  borderBottomWidth: 1,
                  borderBottomStyle: 'solid',
                }}
              >
                <Skeleton.Text size="small" width={64} />
              </Box>
              {METRIC_ROW_WIDTHS.map((width, index) => (
                <Box
                  key={index}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  paddingX="8"
                  borderColor="Border Subtle"
                  style={{
                    gap: 6,
                    height: METRIC_ROW_PX,
                    borderTopWidth: index === 0 ? 0 : 1,
                    borderTopStyle: 'solid',
                  }}
                >
                  <Skeleton.Text size="small" width={width} />
                  <Skeleton.Text size="xsmall" width={120} />
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Layout.MaxWidth>
  </Box>
);
